import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import escalasAvaliacaoComportamental from '@/store/modules/diagnostico/escalasAvaliacaoComportamental';
import escalasAvaliacaoResponsabilidade from '@/store/modules/diagnostico/escalasAvaliacaoResponsabilidade';

const avaliacaoRepository = RepositoryFactory.get('avaliacao');

const defaultState = () => ({
  diagnostico: null,
  diagnosticoComissao: null,
  diagnosticos: [],
  diagnosticosComissao: [],
  dispensas: [],
  dispensasChefia: [],
  dispensasEquipe: [],
  dispensado: true,
  chefiaDispensada: true,
  motivoDispensa: '',
  motivoDispensaChefia: '',
  autoavaliacaoComportamental: [],
  chefiaComportamental: [],
  subordinadoComportamental: [],
  paresComportamental: [],
  autoavaliacaoResponsabilidade: [],
  subordinadoResponsabilidade: [],
  itens: [],
  comportamentos: [],
  responsabilidades: [],
  escalas: {
    comportamental: escalasAvaliacaoComportamental,
    responsabilidade: escalasAvaliacaoResponsabilidade,
  },
  loaded: false,
});

const state = defaultState();

function getMotivoDispensa(dispensa) {
  if (dispensa && dispensa.motivo) {
    return dispensa.motivo.descricao;
  }
  return '';
}

const actions = {
  loadAvaliacoes({ commit, state }, diagnosticoId) {
    return new Promise((resolve) => {
      if (!state.loaded) {
        avaliacaoRepository.getAll(diagnosticoId)
          .then((response) => {
            const dados = response.data;

            if (dados) {
              Object.keys(dados.PERSPECTIVAS).forEach((perspectiva) => {
                Object.keys(dados.PERSPECTIVAS[perspectiva]).forEach((tipo) => {
                  commit(`SET_${tipo}_${perspectiva}`, dados.PERSPECTIVAS[perspectiva][tipo]);
                });
              });

              commit('SET_ITENS', dados.ITENS);
              commit('SET_RESPONSABILIDADES', dados.RESPONSABILIDADES);
              commit('SET_LOADED', true);
            }

            resolve();
          });
      } else {
        resolve();
      }
    });
  },
  setDiagnostico({ commit, state, rootState }, diagnostico) {
    const comportamentos = rootState.comportamento.comportamentosConsolidados.filter(
      (c) => c.mapeamento.id === diagnostico.mapeamento.id,
    );
    commit('mapeamento/SET_MAPEAMENTO_BY_ID', diagnostico.mapeamento.id, { root: true });

    if (state.diagnostico && state.diagnostico.id !== diagnostico.id) {
      commit('SET_LOADED', false);
    }
    commit('SET_DIAGNOSTICO', diagnostico);
    commit('SET_COMPORTAMENTOS', comportamentos);
  },
  setDiagnosticoComissao({ commit }, diagnostico) {
    commit('SET_DIAGNOSTICO_COMISSAO', diagnostico);
  },
  setDiagnosticos({ commit }, diagnosticos) {
    commit('SET_DIAGNOSTICOS', diagnosticos);
  },
  setDiagnosticosComissao({ commit }, diagnosticos) {
    commit('SET_DIAGNOSTICOS_COMISSAO', diagnosticos);
  },
  setAvaliacao({ commit }, payload) {
    commit(`SET_${payload.tipo}_${payload.perspectiva}`, payload.avaliacao);
  },
  setIens({ commit }, payload) {
    commit('SET_ITENS', payload);
  },
};

const getters = {
  getComportamentos: (st) => st.comportamentos,
  getDiagnostico: (st) => (acesso) => {
    switch (acesso) {
      case 'COMUM':
        return st.diagnostico;
      case 'COMISSAO':
        return st.diagnosticoComissao;
      default:
        return null;
    }
  },
  getDiagnosticos: (st) => (acesso) => {
    switch (acesso) {
      case 'COMUM':
        return st.diagnosticos;
      case 'COMISSAO':
        return st.diagnosticosComissao;
      default:
        return [];
    }
  },
  getProgressoAvaliacao: (st) => (tipo, perspectiva) => {
    // prop da avaliação
    const prop = tipo.toLowerCase() + perspectiva[0].toUpperCase() + perspectiva.substring(1).toLowerCase();
    let avaliacoes = st[prop];
    let avaliado = 0;
    let total;

    // Considera apenas gestores e servidores com responsabilidades
    if (perspectiva === 'RESPONSABILIDADE') {
      avaliacoes = avaliacoes.filter(
        (a) => st.responsabilidades.some((r) => r.unidade.id === a.avaliado.unidadeVersao.id),
      );
    }

    // Quantidade de itens avaliados
    avaliado = st.itens.filter((i) => avaliacoes.some((a) => a.id === i.avaliacao.id)).length;

    switch (perspectiva) {
      case 'COMPORTAMENTAL':
        total = avaliacoes.length * st.comportamentos.length;
        break;
      case 'RESPONSABILIDADE':
        if (tipo === 'AUTOAVALIACAO') {
          total = avaliacoes.length * st.responsabilidades
            .filter((r) => avaliacoes.some((a) => a.avaliado.unidadeVersao.id === r.unidade.id)).length;
        } else {
          // Total de itens e quantidade itens avaliados da avalaiação técnica da equipe (Tipo SUBORDINADO)
          const avServidores = avaliacoes.filter((a) => a.avaliado.unidadeVersao.id === a.avaliador.unidadeVersao.id);
          const avGestores = avaliacoes.filter((a) => a.avaliado.unidadeVersao.id !== a.avaliador.unidadeVersao.id);

          // Total de itens levando em conta os servidores
          total = avServidores.length * st.responsabilidades
            .filter((r) => avServidores.some((a) => r.unidade.id === a.avaliado.unidadeVersao.id)).length;

          // Total de itens adicionando a quantidade de cada gestor
          avGestores.forEach(
            (a) => total += st.responsabilidades
              .filter((r) => r.unidade.id === a.avaliado.unidadeVersao.id).length,
          );

          // Quantidade de itens avalaidos dos servidores
          avaliado = st.itens.filter((i) => avServidores.some((a) => a.id === i.avaliacao.id)).length;

          // Quantidade de itens avaliados levando em consideração cada gestor
          avGestores.forEach((a) => avaliado += st.itens.filter((i) => i.avaliacao.id === a.id).length);
        }
        break;
      default: total = 0;
    }

    if (total > 0) {
      return (avaliado / total) * 100;
    }
    return 0;
  },
};

const mutations = {
  SET_DIAGNOSTICO: (st, diagnostico) => {
    st.diagnostico = diagnostico;

    const dispensa = st.dispensas.find((d) => d.diagnostico.id === diagnostico.id);
    const dispensaChefia = st.dispensasChefia.find((d) => d.diagnostico.id === diagnostico.id);
    st.dispensado = dispensa !== undefined;
    st.chefiaDispensada = dispensaChefia !== undefined;
    st.motivoDispensa = getMotivoDispensa(dispensa);
    st.motivoDispensaChefia = getMotivoDispensa(dispensaChefia);
  },
  SET_DIAGNOSTICO_COMISSAO: (st, diagnostico) => {
    st.diagnosticoComissao = diagnostico;
  },
  SET_DIAGNOSTICOS: (st, diagnosticos) => {
    st.diagnosticos = diagnosticos;
  },
  SET_DIAGNOSTICOS_COMISSAO: (st, diagnosticos) => {
    st.diagnosticosComissao = diagnosticos;
  },
  SET_DISPENSAS: (st, dispensas) => {
    st.dispensas = dispensas;
  },
  SET_DISPENSAS_CHEFIA: (st, dispensas) => {
    st.dispensasChefia = dispensas;
  },
  SET_DISPENSAS_EQUIPE: (st, dispensas) => {
    st.dispensasEquipe = dispensas.sort((a, b) => (a.usuario.nome.localeCompare(b.usuario.nome)));
  },
  SET_AUTOAVALIACAO_COMPORTAMENTAL: (st, avaliacao) => {
    st.autoavaliacaoComportamental = avaliacao;
  },
  SET_CHEFIA_COMPORTAMENTAL: (st, avaliacao) => {
    st.chefiaComportamental = avaliacao;
  },
  SET_SUBORDINADO_COMPORTAMENTAL: (st, avaliacoes) => {
    st.subordinadoComportamental = avaliacoes;
  },
  SET_PARES_COMPORTAMENTAL: (st, avaliacoes) => {
    st.paresComportamental = avaliacoes;
  },
  SET_AUTOAVALIACAO_RESPONSABILIDADE: (st, avaliacao) => {
    st.autoavaliacaoResponsabilidade = avaliacao;
  },
  SET_SUBORDINADO_RESPONSABILIDADE: (st, avaliacoes) => {
    st.subordinadoResponsabilidade = avaliacoes;
  },
  SET_ITENS: (st, itens) => {
    st.itens = itens.map((i) => ({
      ...i,
      nota: i.nota || { rating: 0 },
    }));
  },
  SET_COMPORTAMENTOS: (st, comportamentos) => {
    st.comportamentos = comportamentos;
  },
  SET_RESPONSABILIDADES: (st, responsabilidades) => {
    st.responsabilidades = responsabilidades;
  },
  SET_LOADED: (st, loaded) => {
    st.loaded = loaded;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
